/** A global safety net for promises nobody awaited (e.g. a background ping while the network is down): log each
 * distinct reason once as a console warning and keep the app running. Without it the browser reports an
 * "Uncaught (in promise) TypeError: Failed to fetch" error on every page. */
type Target = Pick<Window, "addEventListener" | "removeEventListener">;
type Log = Pick<Console, "warn">;

const MAX_DISTINCT = 50;

function reasonText(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  if (typeof reason === "string") return reason;
  try {
    return JSON.stringify(reason) ?? String(reason);
  } catch {
    return String(reason);
  }
}

export function installRejectionLogger(target: Target = window, log: Log = console): () => void {
  const seen = new Set<string>();
  const onRejection = (e: Event) => {
    e.preventDefault();
    const text = reasonText((e as PromiseRejectionEvent).reason);
    if (seen.has(text) || seen.size >= MAX_DISTINCT) return;
    seen.add(text);
    log.warn(`Ignored an unhandled promise rejection: ${text}`);
  };
  target.addEventListener("unhandledrejection", onRejection);
  return () => target.removeEventListener("unhandledrejection", onRejection);
}
